import {
  AllInclusiveCmsItem,
  CarPark,
  CartCabinI,
  CartItem,
  CartItemPrice,
  CartPassenger,
  DiningSelectionMetadata,
  OBSObjI,
} from '@msc-b2c/interfaces/types';
import { isAllInclusiveByCmsOrTypeCode } from '../postbookingresults/utils/allInclusiveCmsHelpers';
import {
  ExcursionPrepaidBookingPricesMyMSCRequest,
  GetBookingMyMscResponse,
  MyMscExcursion,
  OBSI,
  OBSPrepaidBookingMyMSCRequest,
  OnboardServicePrepaidBookingPricesMyMSCRequest,
  PrepaidBookingPricesMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import { getActiveSession, resolveDateFormat, store } from '@msc-b2c/shared-utils';
import { BookingStatusEnum, ItemTypeEnum } from '@msc-b2c/interfaces/enums';

import dayjs from 'dayjs';
// ---------------------------------------------------------------------------
// Dining metadata helpers (defensive – old cart entries without diningSelection are kept as-is)
// ---------------------------------------------------------------------------

/**
 * Strips a DiningSelectionMetadata object if it is so incomplete that it would
 * produce an invalid booking payload.  Returns undefined for legacy items.
 */
export const sanitizeDiningSelection = (
  diningSelection?: DiningSelectionMetadata
): DiningSelectionMetadata | undefined => {
  if (!diningSelection) return undefined;
  // Must have at least the entry point to be meaningful (mode is resolved during the flow)
  if (!diningSelection.entryPoint) return undefined;
  return diningSelection;
};

/**
 * Returns the dining room booking entries for a cart item's passenger list.
 * Used in getObsForBookingFromCart to attach dining context to the booking API payload.
 * Returns undefined when the item has no valid dining selection (non-dining items, legacy items).
 */
export const buildDiningRoomsForPassenger = (
  diningSelection: DiningSelectionMetadata | undefined
):
  | Array<{
      code?: string;
      slot?: string;
      date?: string;
    }>
  | undefined => {
  const safe = sanitizeDiningSelection(diningSelection);
  if (!safe) return undefined;

  const rooms: Array<{ code?: string; slot?: string; date?: string }> =
    safe.reservations && safe.reservations.length > 0
      ? safe.reservations.map((reservation) => ({
          ...(reservation.restaurantCode && { code: reservation.restaurantCode }),
          ...(reservation.hour && { slot: reservation.hour }),
          ...(reservation.day && { date: reservation.day }),
        }))
      : [
          {
            ...(safe.diningRoomCode && { code: safe.diningRoomCode }),
            ...(safe.time && { slot: safe.time }),
            ...(safe.date && { date: safe.date }),
          },
        ];

  const nonempty = rooms.filter((room) => Object.keys(room).length > 0);
  return nonempty.length > 0 ? nonempty : undefined;
};

const getRelatedServiceCodes = (item: OBSObjI): string[] => {
  const relatedCodes = [
    ...(item.prepaidItems?.serviceCodes?.adults ?? []),
    ...(item.prepaidItems?.serviceCodes?.minors ?? []),
  ];

  return Array.from(new Set(relatedCodes.filter((code): code is string => Boolean(code))));
};

const isAllInclusiveCatalogItem = (catalogItem?: OBSObjI): boolean =>
  Boolean(catalogItem?.prepaidItems?.isAllInclusive);

const createCartItemObj = (
  item: OBSObjI | MyMscExcursion | null,
  passengersIds: CartPassenger[],
  cabinsIds: CartCabinI[],
  relatedItemCode?: string,
  carPark?: CarPark,
  diningSelection?: DiningSelectionMetadata,
  promoBadges?: string,
  itemListName?: string
): CartItem => {
  if (!item) return {} as CartItem;

  const sanitizedDining = sanitizeDiningSelection(diningSelection);
  const { timeslotsSelectionNotAvailable, ...persistedDiningSelection } = sanitizedDining ?? {};

  return {
    itemType: item.startDate ? 'EXCURSION' : 'ONBOARD_SERVICE',
    ...(relatedItemCode && { relatedItemCode }),
    code: item.code,
    ...(item.type && { type: item.type.code }), //for onboard services
    ...(item.deliveryDate && { deliveryDate: item.deliveryDate }), //for onboard services
    ...(item.startDate && { startDate: item.startDate }), //for excursions
    priceBasis: item.priceBasis,
    passengers: passengersIds,
    cabins: cabinsIds,
    ...(item.promotion && { promotion: { id: item.promotion.id } }),
    ...(carPark && { carPark: carPark }),
    ...(sanitizedDining && { diningSelection: persistedDiningSelection }),
    ...(timeslotsSelectionNotAvailable && { timeslotsSelectionNotAvailable }),
    ...(promoBadges && { promoBadges }),
    ...(itemListName && { itemListName }),
  };
};

const isSameCartItem = (cartItem: CartItem, newCartItem: CartItem): boolean => {
  if (cartItem.code !== newCartItem.code) {
    return false;
  }

  if (newCartItem.itemType === 'EXCURSION') {
    // Excursions are uniquely identified by code + startDate.
    return cartItem.startDate === newCartItem.startDate;
  }

  return true;
};

const updateCart = (newCartItem: CartItem, existingCart?: CartItem[]): CartItem[] => {
  const storedCart: CartItem[] = store.getState().myAreaCart.items;
  const updatedCart = existingCart ?? [...storedCart];

  const itemIndex = updatedCart.findIndex((cartItem) => isSameCartItem(cartItem, newCartItem));

  if (itemIndex === -1) {
    // Adding new item to cart when there is no matching identity.
    updatedCart.push(newCartItem);
  } else {
    // Item already exists in cart, replace matching entry.
    updatedCart[itemIndex] = newCartItem;
  }

  return updatedCart;
};

/**
 * Adds an item to the cart stored in the sessionStorage. If the item already exists, it updates the item.
 *
 * @param {OBSObjI | MyMscExcursion | null} item - The item to be added or updated in the cart.
 * @param {Record<string, Record<string, boolean>>} passengers - A nested object where the first key represents the cabin,
 * and the second key represents the passenger ID with a boolean indicating if the passenger is selected.
 * @param {CarPark} [carPark] - Optional car park information to be added to the cart item.
 * @param {Record<string, any>} additionalData - additional data for the cart object.
 * @returns {void}
 */
export const addToCart = (
  item: OBSObjI | MyMscExcursion | null,
  passengers: Record<string, Record<string, boolean>>,
  carPark?: CarPark,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalData?: Record<string, any>
): CartItem[] => {
  if (!item) return [];

  const passengersIds: CartPassenger[] = [];
  const cabinsIds: CartCabinI[] = [];
  Object.entries(passengers).forEach(([cabinKey, cabinPassengers]) => {
    Object.entries(cabinPassengers).forEach(([passengerId, isChecked]) => {
      if (isChecked) {
        const preferredGuideLanguage = additionalData?.selectedLanguages?.[passengerId];
        passengersIds.push({
          id: passengerId,
          preferredGuideLanguage,
        });
        if (!cabinsIds.some((cabin) => cabin.id === cabinKey)) {
          cabinsIds.push({ id: cabinKey, preferredGuideLanguage });
        }
      }
    });
  });

  //create the new cart item
  const newCartItem = createCartItemObj(
    item,
    passengersIds,
    cabinsIds,
    undefined,
    carPark,
    undefined,
    additionalData?.promoBadges,
    additionalData?.itemListName
  );
  return updateCart(newCartItem);
};

/**
 * Dedicated cart entry point for the dining multistep flow.
 * Keeps dining-specific metadata isolated from the generic addToCart path so that
 * non-dining code paths are completely unaffected.
 *
 * @param {OBSObjI | null} item - The restaurant OBS item.
 * @param {Record<string, Record<string, boolean>>} passengers - Selected passenger IDs per cabin.
 * @param {DiningSelectionMetadata} diningSelection - Dining flow metadata (entry point + any resolved values).
 */
export const addDiningItemToCart = (
  item: OBSObjI | null,
  passengers: Record<string, Record<string, boolean>>,
  diningSelection: DiningSelectionMetadata,
  promoBadges?: string,
  itemListName?: string
): CartItem[] => {
  if (!item) return [];

  const passengersIds: CartPassenger[] = [];
  const cabinsIds: CartCabinI[] = [];
  Object.entries(passengers).forEach(([cabinKey, cabinPassengers]) => {
    Object.entries(cabinPassengers).forEach(([passengerId, isChecked]) => {
      if (isChecked) {
        passengersIds.push({ id: passengerId });
        if (!cabinsIds.some((cabin) => cabin.id === cabinKey)) {
          cabinsIds.push({ id: cabinKey });
        }
      }
    });
  });

  const newCartItem = createCartItemObj(
    item,
    passengersIds,
    cabinsIds,
    undefined,
    undefined,
    diningSelection,
    promoBadges,
    itemListName
  );
  return updateCart(newCartItem);
};

/**
 * Adds an ALLINC item and its related items to the cart.
 *
 * @param {OBSI} shopOBS - The shop onboard service object containing price and passenger info.
 * @param {OBSObjI[]} allOBS - Array of all onboard service objects.
 * @param {OBSObjI} item - The base ALLINC item to add to the cart.
 * @returns {CartItem[]} The updated cart array after adding the ALLINC item and its related items.
 */
export const addAllIncItemAndRelatedItemToCart = (
  shopOBS: OBSI,
  allOBS: OBSObjI[],
  item: OBSObjI,
  promoBadges?: string,
  itemListName?: string
): CartItem[] => {
  const storedCart: CartItem[] = store.getState().myAreaCart.items;
  let updatedCart = [...storedCart];

  if (!shopOBS?.prices?.perPassenger?.length || !item?.code) {
    return updatedCart;
  }

  const buildPassengers = (code: string): CartPassenger[] =>
    shopOBS.prices.perPassenger
      .filter((passenger) => passenger.relatedItemCode === code && passenger.id)
      .map((passenger) => ({ id: passenger.id as string }));

  const basePassengers = buildPassengers(item.code);
  updatedCart = updateCart(
    createCartItemObj(
      item,
      basePassengers,
      [],
      undefined,
      undefined,
      undefined,
      promoBadges,
      itemListName
    ),
    updatedCart
  );

  const relatedCodes = getRelatedServiceCodes(item);
  const relatedItem = relatedCodes
    .map((code) => allOBS.find((obs) => obs.code === code))
    .find((obs): obs is OBSObjI => Boolean(obs));
  if (!relatedItem?.code) {
    return updatedCart;
  }

  const relatedPassengers = buildPassengers(relatedItem.code);
  updatedCart = updateCart(
    createCartItemObj(
      relatedItem,
      relatedPassengers,
      [],
      item.code,
      undefined,
      undefined,
      undefined,
      itemListName
    ),
    updatedCart
  );

  return updatedCart;
};

export const hasAllInclusiveOrCombinedInCart = (
  cartItems: CartItem[],
  allOBS?: OBSObjI[]
): boolean => {
  return (
    cartItems?.some((item) => {
      if (item.relatedItemCode) return true;
      const matchingCatalogItem = allOBS?.find((catalogItem) => catalogItem.code === item.code);
      return isAllInclusiveCatalogItem(matchingCatalogItem);
    }) ?? false
  );
};

export const hasAllInclusiveExcursionsInCart = (
  cartItems: CartItem[],
  allInclusiveCmsItems?: AllInclusiveCmsItem[]
): boolean =>
  cartItems?.some(
    (item) =>
      item.itemType === 'EXCURSION' &&
      isAllInclusiveByCmsOrTypeCode(allInclusiveCmsItems, item.code)
  ) ?? false;

export const hasAllInclusiveOrCombinedInBooking = (
  booking: GetBookingMyMscResponse | undefined,
  allOBS?: OBSObjI[]
): boolean => {
  const activeCruiseId = getActiveSession().cruiseId;
  const scopedBookings = activeCruiseId
    ? booking?.prepaid?.bookings?.filter((bookingItem) => bookingItem.cruise?.id === activeCruiseId)
    : booking?.prepaid?.bookings;

  return (
    scopedBookings?.some((bookingItem) =>
      bookingItem.onboardServices?.some(
        (obs) =>
          obs.bookingStatus?.code === BookingStatusEnum.BOOKED &&
          isAllInclusiveCatalogItem(allOBS?.find((catalogItem) => catalogItem.code === obs.code))
      )
    ) ?? false
  );
};

/**
 * Retrieves all excursion items from the cart.
 *
 * @returns {ExcursionPrepaidBookingPricesMyMSCRequest[]} An array of excursion items formatted for prepaid booking price requests.
 */
export const getExcursionsFromCart = (): ExcursionPrepaidBookingPricesMyMSCRequest[] => {
  const cart: CartItem[] = store.getState().myAreaCart.items;
  return cart
    .filter((cartItem) => cartItem.itemType === 'EXCURSION')
    .map((cartItem) => ({
      code: cartItem.code,
      startDate: cartItem.startDate,
      priceBasis: cartItem.priceBasis,
      ...(cartItem.priceBasis === 'PER_PERSON' && {
        passengers: cartItem.passengers.map((passenger) => ({ id: passenger.id })),
      }),
      ...(cartItem.priceBasis === 'PER_CABIN' && {
        cabins: cartItem.cabins.map(({ id }) => ({ id })),
      }),
      ...(cartItem.promotion && { promotion: cartItem.promotion }),
    }));
};

/**
 * Retrieves all onboard service items from the cart for the
 * prepaid prices request.
 *
 * IMPORTANT: This must NOT include `vehicle` or other booking-only
 * fields, as the prices API does not expect them.
 *
 * @returns {OnboardServicePrepaidBookingPricesMyMSCRequest[]} OBS items for price calculation.
 */
export const getObsFromCart = (): OnboardServicePrepaidBookingPricesMyMSCRequest[] => {
  const cart: CartItem[] = store.getState().myAreaCart.items;
  return cart
    .filter((cartItem) => cartItem.itemType === 'ONBOARD_SERVICE')
    .map((cartItem) => {
      const diningRooms = buildDiningRoomsForPassenger(cartItem.diningSelection);

      return {
        code: cartItem.code,
        type: cartItem.type,
        deliveryDate: cartItem.deliveryDate,
        priceBasis: cartItem.priceBasis,
        ...(diningRooms?.length && { diningRooms }),
        ...(cartItem.priceBasis === 'PER_PERSON' && {
          passengers: cartItem.passengers.map((passenger) => ({
            id: passenger.id,
          })),
        }),
        ...(cartItem.priceBasis === 'PER_CABIN' && { cabins: cartItem.cabins }),
        ...(cartItem.promotion && { promotion: cartItem.promotion }),
      };
    });
};

/**
 * Retrieves all onboard service items from the cart for the
 * final prepaid booking request.
 *
 * This CAN include `vehicle` for PARK services, which will be
 * sent only in the booking payload (not in prices).
 *
 * @returns {OBSPrepaidBookingMyMSCRequest[]} OBS items for booking.
 */
export const getObsForBookingFromCart = (): OBSPrepaidBookingMyMSCRequest[] => {
  const cart: CartItem[] = store.getState().myAreaCart.items;
  return cart
    .filter((cartItem) => cartItem.itemType === 'ONBOARD_SERVICE')
    .map((cartItem) => {
      const diningRooms = buildDiningRoomsForPassenger(cartItem.diningSelection);

      return {
        code: cartItem.code,
        type: cartItem.type ?? '',
        deliveryDate: cartItem.deliveryDate ?? '',
        priceBasis: cartItem.priceBasis,
        ...(diningRooms?.length && { diningRooms }),
        ...(cartItem.priceBasis === 'PER_PERSON' && {
          passengers: cartItem.passengers.map((passenger) => ({
            ...passenger,
          })),
        }),
        ...(cartItem.priceBasis === 'PER_CABIN' && { cabins: cartItem.cabins }),
        ...(cartItem.promotion && { promotion: cartItem.promotion }),
        ...(cartItem.type === 'PARK' &&
          cartItem.carPark && {
            vehicle: {
              description: cartItem.carPark.model,
              registrationNumber: cartItem.carPark.plate,
            },
          }),
      };
    });
};

/**
 * Returns the item type for a given code from the cart.
 *
 * @param {string | undefined} code - The unique code of the item.
 * @returns {ItemTypeEnum | null} The type of the item, or null if not found.
 */
export const getItemTypeByCode = (code: string | undefined): ItemTypeEnum | null => {
  if (!code) return null;
  const cart: CartItem[] = store.getState().myAreaCart.items;
  const item = cart.find((cartItem) => cartItem.code === code);
  return item ? (item.itemType as ItemTypeEnum) : null;
};

/**
 * Retrieves the price information for a cart item based on its type and code.
 * The strikethrough price is determined based on the promotion and discount details: if the promo is MARKUP (i.e., the price after discount is higher
 * than the price before discount), no strikethrough price is returned.
 *
 * @param {ItemTypeEnum | null} type - The type of the item (EXCURSION or ONBOARD_SERVICE).
 * @param {string | undefined} code - The unique code of the item.
 * @param {PrepaidBookingPricesMyMSCResponse | null} cartPricesOverride - Optional prices object to use instead of reading from the store (e.g. confirmation response after 3Ds payment).
 * @returns {CartItemPrice | null} The price details for the item, including the discounted price and optional strike-through price, or null if not found.
 */
export const getPriceByTypeAndCode = (
  type: ItemTypeEnum | null,
  code: string | undefined,
  cartPricesOverride?: PrepaidBookingPricesMyMSCResponse | null
): CartItemPrice | null => {
  const cartPrices: PrepaidBookingPricesMyMSCResponse | null =
    cartPricesOverride !== undefined ? cartPricesOverride : store.getState().myAreaCart.prices;
  if (!cartPrices) return null;

  const items =
    type === ItemTypeEnum.EXCURSION
      ? cartPrices.excursions
      : type === ItemTypeEnum.ONBOARD_SERVICE
      ? cartPrices.onboardServices
      : null;

  if (!items) return null;

  const itemPrice = items.find((item) => item.code === code)?.price?.total;
  if (!itemPrice) return null;

  const { afterDiscount = '', beforePromotionAndDiscount, beforeDiscount } = itemPrice;
  const afterDiscountPrice = Number(afterDiscount);
  const beforePromotionAndDiscountPrice = Number(beforePromotionAndDiscount);
  const beforeDiscountPrice = Number(beforeDiscount);

  if (
    afterDiscountPrice > beforePromotionAndDiscountPrice ||
    afterDiscountPrice > beforeDiscountPrice
  ) {
    //PROMO MARKUP: no strikethrough must be displayed
    return {
      price: afterDiscount,
    };
  }
  const strikeThroughPrice =
    beforePromotionAndDiscountPrice && afterDiscountPrice < beforePromotionAndDiscountPrice
      ? beforePromotionAndDiscountPrice
      : afterDiscountPrice < beforeDiscountPrice
      ? beforeDiscountPrice
      : undefined;

  return {
    price: afterDiscount,
    ...(strikeThroughPrice !== undefined && { strikeThroughPrice: String(strikeThroughPrice) }),
  };
};

/**
 * Check if an item is into the cart.
 *
 * @param {MyMscExcursion | OBSObjI} item - an OBS or excursion
 * @param { CartItem[] } cart - the cart items list
 *
 * @return boolean - true if the item is into the cart, false otherwise.
 */
export const isItemOnCart = (item: MyMscExcursion | OBSObjI, cart: CartItem[]): boolean => {
  const itemStartDate = 'startDate' in item ? item.startDate : undefined;

  return cart.some((cartItem) => {
    if (cartItem.code !== item.code) {
      return false;
    }

    if (itemStartDate && cartItem.startDate) {
      return cartItem.startDate === itemStartDate;
    }

    return true;
  });
};

/**
 * Check if an item is into the cart.
 *
 * @param {MyMscExcursion | OBSObjI} item - an OBS or excursion
 * @param { CartItem[] } cart - the cart items list
 *
 * @return boolean - true if the item is into the cart, false otherwise.
 */
export const getItemsFromCart = (
  item: MyMscExcursion | OBSObjI,
  cart: CartItem[]
): CartItem[] | undefined => {
  const itemStartDate = 'startDate' in item ? item.startDate : undefined;

  return cart.filter((cartItem) => {
    if (cartItem.code !== item.code) {
      return false;
    }

    if (itemStartDate && cartItem.startDate) {
      return cartItem.startDate === itemStartDate;
    }

    return true;
  });
};

/** Cart lines matching a prepaid card (direct code, related code, or shared base package). */
export const getCartItemsForPrepaidCard = (
  card: MyMscExcursion | OBSObjI,
  cartItems: CartItem[]
): CartItem[] => {
  if (!card.code || !cartItems.length) {
    return [];
  }

  const itemStartDate = 'startDate' in card ? card.startDate : undefined;
  const directItems = cartItems.filter(
    (item) =>
      (item.code === card.code && item.itemType !== 'EXCURSION') ||
      (item.code === card.code &&
        item.itemType === 'EXCURSION' &&
        itemStartDate !== undefined &&
        item.startDate === itemStartDate)
  );
  const relatedItems = cartItems.filter((item) => item.relatedItemCode === card.code);
  const baseCode = directItems[0]?.relatedItemCode;
  const baseItems = baseCode ? cartItems.filter((item) => item.code === baseCode) : [];

  return [...directItems, ...relatedItems, ...baseItems];
};

/** A cart row plus its linked package lines (e.g. a minor ALLINC linked to its adult). */
export interface MergedCartDisplayItem {
  parent: CartItem;
  related: CartItem[];
}

/**
 * Groups cart lines so a combined package (an adult line + its minor line(s), linked via
 * `relatedItemCode`) renders as a single card. Standalone lines (and any orphan
 * related line whose parent is missing) become their own group.
 */
export const getMergedCartDisplayItems = (cartItems: CartItem[]): MergedCartDisplayItem[] => {
  const parents = cartItems.filter((item) => !item.relatedItemCode);
  const parentCodes = new Set(parents.map((parent) => parent.code));

  const groups: MergedCartDisplayItem[] = parents.map((parent) => ({
    parent,
    related: cartItems.filter((item) => item.relatedItemCode === parent.code),
  }));

  cartItems
    .filter((item) => item.relatedItemCode && !parentCodes.has(item.relatedItemCode))
    .forEach((orphan) => groups.push({ parent: orphan, related: [] }));

  return groups;
};

/**
 * Checks if an item has already been purchased.
 *
 * @param {MyMscExcursion | OBSObjI} item - an OBS or excursion
 * @param {GetBookingMyMscResponse} booking - the booking object
 *
 * @returns {boolean} - true if the item has already been purchased, false otherwise
 */
export const isItemAlreadyPurchased = (
  item: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse
): boolean => {
  const itemStartDate = 'startDate' in item ? item.startDate : undefined;
  const activeCruiseId = getActiveSession().cruiseId;
  const bookings = activeCruiseId
    ? booking?.prepaid?.bookings?.filter((bookingItem) => bookingItem.cruise?.id === activeCruiseId)
    : booking?.prepaid?.bookings;
  if (!bookings?.length) return false;

  return bookings.some((b) =>
    [...(b.excursions ?? []), ...(b.onboardServices ?? [])].some((pack) => {
      if (item.code !== pack.code) {
        return false;
      }

      if (itemStartDate && 'startDate' in pack && pack.startDate) {
        // format the 2 dates with the same basis
        const format = resolveDateFormat();
        return dayjs(pack.startDate).format(format) === dayjs(itemStartDate).format(format);
      }

      return true;
    })
  );
};

/**
 * Filters packages that are either in the cart or already purchased.
 *
 * @param {(MyMscExcursion | OBSObjI)[]} packages - The list of packages to filter.
 * @param {GetBookingMyMscResponse | undefined} booking - The booking information to check against.
 * @param {CartItem[]} cart - The cart information to check against.
 *
 * @returns {(MyMscExcursion | OBSObjI)[]} The filtered list of packages.
 */
export const filterPackagesOnCartOrAlreadyPurchased = (
  packages: (MyMscExcursion | OBSObjI)[],
  booking: GetBookingMyMscResponse | undefined,
  cart: CartItem[]
): (MyMscExcursion | OBSObjI)[] =>
  packages.filter(
    (item) => !(isItemOnCart(item, cart) || (booking && isItemAlreadyPurchased(item, booking)))
  );
